(function () {
  const SELECTORS = {
    root: 'preorder-info-modal[data-preorder-info]',
    trigger: '[data-preorder-info-trigger]',
    overlay: '[data-preorder-info-overlay]',
    dialog: '[data-preorder-info-dialog]',
    close: '[data-preorder-info-close]',
    content: '[data-preorder-info-content]'
  };

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function isVisible(element) {
    if (!element || element.hidden || element.closest('[hidden], .preorder-widget--hidden')) {
      return false;
    }

    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  class PreorderInfoModal extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.lastTrigger = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.addEventListener('click', this.handleClick);
      document.addEventListener('keydown', this.handleKeydown);
      this.setActive(this.dataset.preorderInfoActive === 'true');
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('keydown', this.handleKeydown);
      this.initialized = false;
      this.close({ restoreFocus: false });
    }

    handleClick(event) {
      const close = event.target.closest(SELECTORS.close);
      if (close && this.contains(close)) {
        event.preventDefault();
        this.close();
        return;
      }

      const trigger = event.target.closest(SELECTORS.trigger);
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        this.open(trigger);
        return;
      }

      if (event.target === this.overlay) {
        this.close();
      }
    }

    handleKeydown(event) {
      if (!this.isOpen()) return;

      if (event.key === 'Escape' || event.code === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this.focusableElements;
      if (!focusable.length) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === first || !this.dialog.contains(activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open(trigger) {
      if (this.hidden || !this.overlay || !this.dialog) return;

      this.lastTrigger = trigger;
      this.overlay.hidden = false;
      this.classList.add('preorder-info--open');
      document.documentElement.classList.add('preorder-info-modal-open');

      requestAnimationFrame(() => {
        const firstFocus = this.querySelector(SELECTORS.close) || this.dialog;
        firstFocus.focus();
      });
    }

    close(options = {}) {
      const { restoreFocus = true } = options;

      if (this.overlay) {
        this.overlay.hidden = true;
      }

      this.classList.remove('preorder-info--open');
      document.documentElement.classList.remove('preorder-info-modal-open');

      if (restoreFocus && this.lastTrigger && this.lastTrigger.isConnected) {
        this.lastTrigger.focus();
      }
    }

    setActive(active) {
      if (!active && this.isOpen()) {
        this.close();
      }

      this.hidden = !active;
      this.classList.toggle('preorder-info--hidden', !active);
      this.dataset.preorderInfoActive = active ? 'true' : 'false';

      const trigger = this.querySelector(SELECTORS.trigger);
      if (trigger) {
        trigger.disabled = !active;
      }

    }

    syncFrom(source) {
      const sourceTrigger = source.querySelector(SELECTORS.trigger);
      const targetTrigger = this.querySelector(SELECTORS.trigger);
      if (sourceTrigger && targetTrigger) {
        targetTrigger.innerHTML = sourceTrigger.innerHTML;
        targetTrigger.setAttribute('aria-controls', sourceTrigger.getAttribute('aria-controls'));
      }

      const sourceDialog = source.querySelector(SELECTORS.dialog);
      if (sourceDialog && this.dialog) {
        this.dialog.setAttribute('aria-label', sourceDialog.getAttribute('aria-label') || '');
      }

      const sourceContent = source.querySelector(SELECTORS.content);
      const targetContent = this.querySelector(SELECTORS.content);
      if (sourceContent && targetContent) {
        targetContent.innerHTML = sourceContent.innerHTML;
      }

      if (source.dataset.preorderInfoCurrentVariant) {
        this.dataset.preorderInfoCurrentVariant = source.dataset.preorderInfoCurrentVariant;
      }

      this.setActive(source.dataset.preorderInfoActive === 'true');
    }

    isOpen() {
      return Boolean(this.overlay && !this.overlay.hidden);
    }

    get overlay() {
      return this.querySelector(SELECTORS.overlay);
    }

    get dialog() {
      return this.querySelector(SELECTORS.dialog);
    }

    get focusableElements() {
      if (!this.dialog) return [];

      return Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
    }
  }

  if (!customElements.get('preorder-info-modal')) {
    customElements.define('preorder-info-modal', PreorderInfoModal);
  }

  function syncVariantChange(event) {
    const html = event?.data?.html;
    if (!html) return;

    html.querySelectorAll(SELECTORS.root).forEach((source) => {
      if (!source.id) return;

      const current = document.getElementById(source.id);
      if (current && typeof current.syncFrom === 'function') {
        current.syncFrom(source);
      }
    });
  }

  let isSubscribed = false;
  function subscribeToVariantChanges() {
    if (isSubscribed) return;

    if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.variantChange) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, syncVariantChange);
      isSubscribed = true;
      return;
    }

    window.setTimeout(subscribeToVariantChanges, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', subscribeToVariantChanges);
  } else {
    subscribeToVariantChanges();
  }
})();
